import { useMemo, memo, useCallback } from 'react';
import {
  Table,
  TypographyWithTooltip,
  useTranslations,
  IUseTableReturn,
  IUseQueryTableFetchReturn,
  IUseTableLocalizationReturn,
  IMaterialTableOptions,
  IMaterialTableProps,
} from '@uniqkey-frontend/shared-app';
import {
  GroupsResponseGroup,
} from '@uniqkey-backend-partner/api-client';
import { useNavigate } from 'react-router-dom';
import PageRouteEnum from '../../../enums/PageRouteEnum';
import { TGetGroupsMethod } from '../../../hooks/useGroupsAPI/interfaces';

export interface IGroupsTableRow extends GroupsResponseGroup {}

interface IGroupsTableProps extends
  Omit<IUseTableReturn<IGroupsTableRow>, 'selectedRows' | 'resetTableMethods'>,
  Omit<IUseQueryTableFetchReturn<TGetGroupsMethod>, 'data' | 'resetQuery' | 'isFetchedAfterMount'>
{
  groups: IUseQueryTableFetchReturn<TGetGroupsMethod>['data'];
  selectedGroups: IUseTableReturn<IGroupsTableRow>['selectedRows'];
  localization: IUseTableLocalizationReturn['localization'];
  options?: IMaterialTableOptions<IGroupsTableRow>;
}

const GroupsTable = (props: IGroupsTableProps) => {
  const {
    groups,
    isLoading,
    total,
    localization,
    activePage,
    setActivePage,
    perPage,
    onPerPageChange,
    columnOrderBy,
    onColumnOrderByChange,
    columnOrderDirection,
    onColumnOrderDirectionChange,
    selectedGroups,
    onRowSelectionChange,
    createColumns,
    options,
  } = props;

  const { t } = useTranslations();
  const navigate = useNavigate();

  const columns = useMemo(() => createColumns([
    {
      title: t('groupsPage.name'),
      field: 'name',
      render: (rowData) => (
        <TypographyWithTooltip variant="body2">
          {rowData.name}
        </TypographyWithTooltip>
      ),
      width: '30%',
    },
    {
      title: t('groupsPage.description'),
      field: 'description',
      render: (rowData) => (
        <TypographyWithTooltip variant="body2">
          {rowData.description}
        </TypographyWithTooltip>
      ),
      width: '40%',
      sorting: false,
    },
    {
      title: t('groupsPage.userAmount'),
      field: 'userAmount',
      render: (rowData) => (
        <TypographyWithTooltip variant="body2">
          {rowData.userAmount}
        </TypographyWithTooltip>
      ),
      width: '15%',
    },
    {
      title: t('groupsPage.organizationAmount'),
      field: 'organizationAmount',
      render: (rowData) => (
        <TypographyWithTooltip variant="body2">
          {rowData.organizationAmount}
        </TypographyWithTooltip>
      ),
      width: '15%',
    },
  ]), [createColumns, t]);

  const handleGroupRowClick = useCallback<
    NonNullable<IMaterialTableProps<IGroupsTableRow>['onRowClick']>
  >((event, group) => {
    navigate(`${PageRouteEnum.Groups}/${group!.groupId}`);
  }, [navigate]);

  return (
    <Table
      t={t}
      totalItems={total}
      columns={columns}
      data={groups}
      isLoading={isLoading}
      options={options}
      activePage={activePage}
      onActivePageChange={setActivePage}
      perPage={perPage}
      onPerPageChange={onPerPageChange}
      columnOrderBy={columnOrderBy}
      onColumnOrderByChange={onColumnOrderByChange}
      columnOrderDirection={columnOrderDirection}
      onColumnOrderDirectionChange={onColumnOrderDirectionChange}
      selectedRows={selectedGroups}
      onSelectionChange={onRowSelectionChange}
      localization={localization}
      onRowClick={handleGroupRowClick}
      rowSelectByKey="groupId"
      rowHeight={51}
    />
  );
};

export default memo(GroupsTable);
